import type { Deal } from './types';

/** Status auxiliares filtráveis na toolbar (espelham AuxiliaryStatus, menos ClosedToPortfolio). */
export type StatusFilter = 'Active' | 'Paused' | 'Discarded';

/** Os três status que compõem "Todos" — a ordem define a ordem do merge (ver usePipelineDeals). */
export const ALL_STATUS_FILTERS: StatusFilter[] = ['Active', 'Paused', 'Discarded'];

/**
 * Valor do filtro de status na toolbar: um status isolado ou 'All' (os três juntos).
 * 'All' NÃO é um status da API — é resolvido no cliente buscando os três em paralelo
 * (ver usePipelineDeals). ClosedToPortfolio segue fora do Pipeline em qualquer seleção (vive no
 * Portfólio, que tem export próprio — sem sobreposição de CSV entre os dois).
 */
export type StatusFilterValue = StatusFilter | 'All';

export interface PipelineFilters {
  /** Nomes de tese selecionados (multi). */
  theses: string[];
  /** Id do líder (single) ou null = todos. */
  leadUserId: string | null;
  /** Status auxiliar, ou 'All' para os três; default 'Active'. */
  status: StatusFilterValue;
  priorityOnly: boolean;
}

export const DEFAULT_FILTERS: PipelineFilters = {
  theses: [],
  leadUserId: null,
  status: 'Active',
  priorityOnly: false,
};

/** Opções reais de status (mock: pipeline.jsx:226-230). Usadas pelo export CSV (rótulo por status)
 *  e como base do chip da toolbar — NÃO inclui 'Todos' (que não é um status real). */
export const STATUS_OPTIONS: { id: StatusFilter; label: string }[] = [
  { id: 'Active', label: 'Ativo' },
  { id: 'Paused', label: 'Em pausa' },
  { id: 'Discarded', label: 'Descartado' },
];

/**
 * Opções do chip de Status na toolbar: as 3 reais + "Todos" (os três juntos) como 4ª opção
 * explícita. Desvio de mock aprovado (o mock — pipeline.jsx:226 — não previa "Todos"); mantém o
 * default idêntico ao mock (chip apagado, "ativo" via allLabel) porque 'Active' continua sendo o
 * caminho `null` do SingleSelect, e não 'All' — só o rótulo/estado do CHIP muda com "Todos"
 * selecionado, o valor default do filtro não.
 */
export const STATUS_FILTER_OPTIONS: { id: StatusFilterValue; label: string }[] = [
  ...STATUS_OPTIONS,
  { id: 'All', label: 'Todos' },
];

const STATUS_LABEL_MAP = new Map(STATUS_OPTIONS.map((s) => [s.id, s.label]));

/** Rótulo em pt-BR de um status auxiliar (mesma fonte que exportCsv.ts usa, sem duplicar a lógica
 *  em cada consumidor — DealCard/DealsTable usam para a tag de status, PR-F2). Cai para o próprio
 *  valor se não reconhecido (ex.: ClosedToPortfolio, que não é status filtrável do Pipeline). */
export function statusLabel(status: string): string {
  return STATUS_LABEL_MAP.get(status as StatusFilter) ?? status;
}

/** Aplica os filtros da toolbar sobre os deals (client-side; cabe na escala da Vega). */
export function applyFilters(deals: Deal[], f: PipelineFilters): Deal[] {
  return deals.filter((d) => {
    if (f.status !== 'All' && d.auxiliaryStatus !== f.status) return false;
    if (f.priorityOnly && !d.isPriority) return false;
    if (f.leadUserId && d.leadUserId !== f.leadUserId) return false;
    if (f.theses.length > 0 && !d.thesisNames.some((t) => f.theses.includes(t))) return false;
    return true;
  });
}

/** Opções de líder derivadas dos deals carregados (distintas), ordenadas por nome pt-BR. */
export function deriveLeadOptions(deals: Deal[]): { id: string; label: string }[] {
  const map = new Map<string, string>();
  for (const d of deals) if (!map.has(d.leadUserId)) map.set(d.leadUserId, d.leadUserName);
  return [...map.entries()]
    .map(([id, label]) => ({ id, label }))
    .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
}

/** Opções de tese derivadas dos deals carregados (distintas), ordenadas. */
export function deriveThesisOptions(deals: Deal[]): string[] {
  const set = new Set<string>();
  for (const d of deals) for (const t of d.thesisNames) set.add(t);
  return [...set].sort((a, b) => a.localeCompare(b, 'pt-BR'));
}

/** Conta filtros ativos (para o badge do chip "Limpar"). */
export function activeFilterCount(f: PipelineFilters): number {
  return (
    (f.theses.length > 0 ? 1 : 0) +
    (f.leadUserId ? 1 : 0) +
    (f.status !== 'Active' ? 1 : 0) +
    (f.priorityOnly ? 1 : 0)
  );
}

/** Rótulo do contador de deals na toolbar, seguindo o status selecionado (achado da revisão do
 *  plano — "Deals ativos" mentia hoje para Em pausa/Descartado, não só para "Todos"). */
export function dealsCountLabel(status: StatusFilterValue): string {
  switch (status) {
    case 'All':
      return 'Deals';
    case 'Paused':
      return 'Deals em pausa';
    case 'Discarded':
      return 'Deals descartados';
    case 'Active':
    default:
      return 'Deals ativos';
  }
}
